import CLIENT from "../../lib/api/client";
import ENDPOINTS from "../../lib/api/endpoints";
import { getApiErrorStatus } from "../../lib/api/apiError";
import { sapphirePageParams, sharedPageParams } from "./financePage";

const WITHDRAWAL_COUNTRY = "GH";
const LEGACY_BANK_TYPE = "BANK_ACCOUNT";

export {
  fetchWalletBalance,
  fetchWallets,
  fetchWalletTopUpStatus,
  initiateWalletTopUp,
} from "../checkout/paymentsService";

const retryOnBadRequest = async (attempt, fallback) => {
  try {
    return await attempt();
  } catch (err) {
    if (getApiErrorStatus(err) !== 400) throw err;

    return fallback();
  }
};

export const fetchMyTransactions = async ({
  page = 1,
  pageSize = 20,
  gateway,
  purpose,
  startDate,
  endDate,
} = {}) => {
  const { data } = await CLIENT.get(ENDPOINTS.TRANSACTIONS.mine, {
    params: {
      ...sharedPageParams({ page, pageSize }),
      ...(gateway ? { gateway } : {}),
      ...(purpose ? { purpose } : {}),
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
    },
  });

  return data;
};

export const fetchTransferRecipients = async () => {
  const { data } = await CLIENT.get(ENDPOINTS.TRANSFER_RECIPIENTS.root);

  return data;
};

export const createTransferRecipient = async (payload) => {
  const body = {
    ...payload,
    accountName: String(payload?.accountName ?? "").trim(),
    accountNumber: String(payload?.accountNumber ?? "").replace(/\s+/g, ""),
    countryCode: WITHDRAWAL_COUNTRY,
  };

  const post = () => CLIENT.post(ENDPOINTS.TRANSFER_RECIPIENTS.root, body);

  const { data } =
    body.type === "BANK"
      ? await retryOnBadRequest(post, () =>
          CLIENT.post(ENDPOINTS.TRANSFER_RECIPIENTS.root, { ...body, type: LEGACY_BANK_TYPE })
        )
      : await post();

  return data;
};

export const deleteTransferRecipient = async (id) => {
  const { data } = await CLIENT.delete(ENDPOINTS.TRANSFER_RECIPIENTS.byId(id));

  return data;
};

export const setDefaultTransferRecipient = async (id) => {
  const { data } = await CLIENT.put(ENDPOINTS.TRANSFER_RECIPIENTS.setDefault(id), {});

  return data;
};

export const fetchBanks = async (countryCode = WITHDRAWAL_COUNTRY) => {
  const { data } = await CLIENT.get(ENDPOINTS.BANKING.banks, { params: { countryCode } });

  return data;
};

export const fetchMobileMoneyOperators = async (countryCode = WITHDRAWAL_COUNTRY) => {
  const { data } = await CLIENT.get(ENDPOINTS.BANKING.mobileMoneyOperators, {
    params: { countryCode },
  });

  return data;
};

const payoutBodies = ({ transferRecipientId, value, currency }) => [
  { transferRecipientId, amount: { value, currency } },
  { transferRecipientId, value, currency },
];

export const evaluatePayoutFee = async (input) => {
  const [nested, flat] = payoutBodies(input);

  const { data } = await retryOnBadRequest(
    () => CLIENT.post(ENDPOINTS.PAYOUTS.evaluateFee, nested),
    () => CLIENT.post(ENDPOINTS.PAYOUTS.evaluateFee, flat)
  );

  return data;
};

export const requestPayout = async (input) => {
  const [nested, flat] = payoutBodies(input);

  const { data } = await retryOnBadRequest(
    () => CLIENT.post(ENDPOINTS.PAYOUTS.request, nested),
    () => CLIENT.post(ENDPOINTS.PAYOUTS.request, flat)
  );

  return data;
};

export const fetchPayouts = async ({ page = 0, size = 20, sort = "createdAt,desc", status } = {}) => {
  const { data } = await CLIENT.get(ENDPOINTS.PAYOUTS.root, {
    params: {
      ...sapphirePageParams({ page, size, sort }),
      ...(status ? { status } : {}),
    },
  });

  return data;
};

export const fetchPayout = async (id) => {
  const { data } = await CLIENT.get(ENDPOINTS.PAYOUTS.byId(id));

  return data;
};

export const cancelPayout = async (id) => {
  const { data } = await CLIENT.delete(ENDPOINTS.PAYOUTS.byId(id));

  return data;
};
